import { Modal, Form, DatePicker, Select, Upload, Button, App as AntdApp } from "antd";
import { UploadOutlined } from "@ant-design/icons";
import { useApiUrl } from "@refinedev/core";
import { useTranslation } from "react-i18next";
import { useEffect, useState } from "react";
import type { UploadFile } from "antd/es/upload/interface";
import dayjs from "dayjs";
import { tokenService } from "../../../services/storage/tokenService";
import axios from "axios";
import { useAssignableAuditors } from "./useAssignableAuditors";

interface RouteUploadModalProps {
  visible: boolean;
  onClose: () => void;
  planningId: number;
  auditors: Array<{ id: number; name: string }>;
  auditCompanyId?: number | null;
  countryId?: number | null;
  isAuditorOnly?: boolean;
  currentUser?: { id: number; name: string } | null;
  onSuccess?: () => void;
}

interface RouteFormValues {
  route_date: dayjs.Dayjs;
  auditor: number;
  gpx_file: UploadFile[];
}

/**
 * RouteUploadModal Component
 * Modal for uploading GPS route files (GPX or KML) for auditors.
 * El backend distingue GPX y KML por extensión y extrae el track
 * con el parser adecuado — el frontend solo necesita aceptar
 * ambas extensiones y enviar el nombre original.
 */
const ALLOWED_EXTS = [".gpx", ".kml"];
const ACCEPT_ATTR = ALLOWED_EXTS.join(",");

const hasAllowedExt = (name: string) =>
  ALLOWED_EXTS.some((ext) => name.toLowerCase().endsWith(ext));
export const RouteUploadModal = ({
  visible,
  onClose,
  planningId,
  auditors,
  auditCompanyId,
  countryId,
  isAuditorOnly = false,
  currentUser,
  onSuccess,
}: RouteUploadModalProps) => {
  const { t } = useTranslation();
  const apiUrl = useApiUrl();
  const [form] = Form.useForm<RouteFormValues>();
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [loading, setLoading] = useState(false);
  // Antd v5 deprecó el `message` estático: usar el del contexto
  // <App> para que los toasts se rendericen dentro del provider de
  // theme/locale. Sin esto los success/error se "tragan" silenciosamente
  // en componentes que renderizan vía portal (como este Modal).
  const { message: notify } = AntdApp.useApp();

  const auditorPicker = useAssignableAuditors({
    visible,
    auditCompanyId,
    countryId,
    isAuditorOnly,
    currentUser,
    fallback: auditors,
  });

  // When the picker is locked to the current user (type=5), preselect
  // them so the form is one click shorter.
  useEffect(() => {
    if (visible && auditorPicker.locked && auditorPicker.defaultValue != null) {
      form.setFieldsValue({ auditor: auditorPicker.defaultValue });
    }
  }, [visible, auditorPicker.locked, auditorPicker.defaultValue, form]);

  const handleSubmit = async (values: RouteFormValues) => {
    if (fileList.length === 0) {
      notify.error(
        t("route.route_file_required") ||
          "El archivo de ruta es requerido (GPX o KML)",
      );
      return;
    }

    const file = fileList[0].originFileObj;
    if (!file) {
      notify.error("No se pudo obtener el archivo");
      return;
    }

    setLoading(true);

    try {
      // Read file as base64
      const fileContent = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
          const base64 = reader.result as string;
          // Remove data URL prefix (e.g., "data:application/gpx+xml;base64,")
          const base64Content = base64.split(",")[1];
          resolve(base64Content);
        };
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });

      // Get access token
      const accessToken = tokenService.getAccessToken();

      // Send as JSON with base64 encoded file
      const response = await axios.post(
        `${apiUrl}/routes/`,
        {
          route_date: values.route_date.format("YYYY-MM-DD"),
          auditor: values.auditor,
          planning: planningId,
          gpx_file_content: fileContent,
          gpx_file_name: file.name,
        },
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
            "Content-Type": "application/json",
          },
        }
      );

      notify.success(t("route.upload_success") || "Ruta subida exitosamente");
      form.resetFields();
      setFileList([]);
      onClose();
      if (onSuccess) {
        onSuccess();
      }
    } catch (error: any) {
      console.error("Upload error:", error);
      const errorMessage =
        error?.response?.data?.gpx_file?.[0] ||
        error?.response?.data?.route_date?.[0] ||
        error?.response?.data?.auditor?.[0] ||
        error?.response?.data?.non_field_errors?.[0] ||
        error?.response?.data?.detail ||
        error?.message ||
        t("route.upload_error") ||
        "Error al subir la ruta";
      notify.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    form.resetFields();
    setFileList([]);
    onClose();
  };

  // Custom upload validation
  const beforeUpload = (file: File) => {
    if (!hasAllowedExt(file.name)) {
      notify.error(
        t("route.invalid_route_file") ||
          "El archivo debe ser GPX (.gpx) o KML (.kml)",
      );
      return Upload.LIST_IGNORE;
    }
    return false; // Prevent auto upload
  };

  const handleFileChange = (info: any) => {
    let newFileList = [...info.fileList];
    // Limit to 1 file
    newFileList = newFileList.slice(-1);
    setFileList(newFileList);
  };

  return (
    <Modal
      title={t("route.upload_route") || "Subir Ruta GPS"}
      open={visible}
      onCancel={handleCancel}
      onOk={() => form.submit()}
      okText={t("common.save") || "Guardar"}
      cancelText={t("common.cancel") || "Cancelar"}
      confirmLoading={loading}
      width={600}
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        autoComplete="off"
      >
        <Form.Item
          name="route_date"
          label={t("route.route_date") || "Fecha de Ruta"}
          rules={[
            {
              required: true,
              message: `${t("route.route_date") || "Fecha de Ruta"} ${t("common.required") || "es requerido"}`,
            },
          ]}
        >
          <DatePicker
            style={{ width: "100%" }}
            format="DD/MM/YYYY"
            placeholder={`${t("common.select")} ${t("route.route_date") || "fecha de ruta"}`}
          />
        </Form.Item>

        <Form.Item
          name="auditor"
          label={t("route.auditor") || "Auditor"}
          rules={[
            {
              required: true,
              message: `${t("route.auditor") || "Auditor"} ${t("common.required") || "es requerido"}`,
            },
          ]}
        >
          <Select
            placeholder={`${t("common.select")} ${t("route.auditor") || "auditor"}`}
            showSearch
            optionFilterProp="label"
            loading={auditorPicker.loading}
            disabled={auditorPicker.locked}
            getPopupContainer={(trigger) => trigger.parentElement || document.body}
            options={auditorPicker.options}
          />
        </Form.Item>

        <Form.Item
          name="gpx_file"
          label={t("route.route_file") || "Archivo de ruta (GPX o KML)"}
          rules={[
            {
              required: true,
              message:
                t("route.route_file_required") ||
                "El archivo de ruta es requerido (GPX o KML)",
            },
          ]}
        >
          <Upload
            fileList={fileList}
            beforeUpload={beforeUpload}
            onChange={handleFileChange}
            maxCount={1}
            accept={ACCEPT_ATTR}
          >
            <Button icon={<UploadOutlined />}>
              {t("route.select_route_file") || "Seleccionar archivo (.gpx / .kml)"}
            </Button>
          </Upload>
        </Form.Item>
      </Form>
    </Modal>
  );
};
